import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  QueueDashboardState,
  QueueDashboardOverview,
  HourlyDistribution,
  LocationPerformanceData,
  ProductBreakdownData,
  FactoryBreakdownData,
  DailyTrendData,
  WeighbridgeStatsData,
  StuckOrdersData,
  DateRange,
  DatePreset,
  QueueDashboardFilters,
  FetchQueueDashboardRequest,
} from '../domain/types'
import { DEFAULT_DATE_RANGE } from '../domain/constants'

const initialState: QueueDashboardState = {
  overview: null,
  hourlyDistribution: null,
  locationPerformance: null,
  productBreakdown: null,
  factoryBreakdown: null,
  dailyTrend: null,
  weighbridgeStats: null,
  stuckOrders: null,
  dateRange: DEFAULT_DATE_RANGE,
  filters: {},
  loading: {
    overview: false,
    hourly: false,
    locations: false,
    products: false,
    factories: false,
    dailyTrend: false,
    weighbridge: false,
    stuckOrders: false,
  },
  error: { message: null },
  lastRefreshed: null,
}

export const queueDashboardSlice = createSlice({
  name: 'queueDashboard',
  initialState,
  reducers: {
    // Fetch all data
    fetchAll: (state, _action: PayloadAction<FetchQueueDashboardRequest | undefined>) => {
      state.loading = { ...state.loading, overview: true, hourly: true, locations: true, products: true, factories: true, dailyTrend: true, weighbridge: true }
      state.error.message = null
    },

    // Overview
    fetchOverviewSuccess: (state, action: PayloadAction<QueueDashboardOverview>) => {
      state.overview = action.payload
      state.loading.overview = false
      state.lastRefreshed = new Date().toISOString()
    },
    fetchOverviewFailure: (state, action: PayloadAction<string>) => {
      state.loading.overview = false
      state.error.message = action.payload
    },

    // Hourly
    fetchHourlySuccess: (state, action: PayloadAction<HourlyDistribution>) => {
      state.hourlyDistribution = action.payload
      state.loading.hourly = false
    },
    fetchHourlyFailure: (state, action: PayloadAction<string>) => {
      state.loading.hourly = false
      state.error.message = action.payload
    },

    // Locations
    fetchLocationsSuccess: (state, action: PayloadAction<LocationPerformanceData>) => {
      state.locationPerformance = action.payload
      state.loading.locations = false
    },
    fetchLocationsFailure: (state, action: PayloadAction<string>) => {
      state.loading.locations = false
      state.error.message = action.payload
    },

    // Products — on their own, for pages that show only the product breakdown
    // (the admin main dashboard) and must not pull all seven endpoints for it.
    fetchProducts: (state, _action: PayloadAction<FetchQueueDashboardRequest | undefined>) => {
      state.loading.products = true
      state.error.message = null
    },
    fetchProductsSuccess: (state, action: PayloadAction<ProductBreakdownData>) => {
      state.productBreakdown = action.payload
      state.loading.products = false
    },
    fetchProductsFailure: (state, action: PayloadAction<string>) => {
      state.loading.products = false
      state.error.message = action.payload
    },

    // Factories
    fetchFactoriesSuccess: (state, action: PayloadAction<FactoryBreakdownData>) => {
      state.factoryBreakdown = action.payload
      state.loading.factories = false
    },
    fetchFactoriesFailure: (state, action: PayloadAction<string>) => {
      state.loading.factories = false
      state.error.message = action.payload
    },

    // Daily Trend
    fetchDailyTrendSuccess: (state, action: PayloadAction<DailyTrendData>) => {
      state.dailyTrend = action.payload
      state.loading.dailyTrend = false
    },
    fetchDailyTrendFailure: (state, action: PayloadAction<string>) => {
      state.loading.dailyTrend = false
      state.error.message = action.payload
    },

    // Weighbridge Stats
    fetchWeighbridgeSuccess: (state, action: PayloadAction<WeighbridgeStatsData>) => {
      state.weighbridgeStats = action.payload
      state.loading.weighbridge = false
    },
    fetchWeighbridgeFailure: (state, action: PayloadAction<string>) => {
      state.loading.weighbridge = false
      state.error.message = action.payload
    },

    // Stuck orders — polled on its own, independent of the date window.
    fetchStuckOrders: (state, _action: PayloadAction<FetchQueueDashboardRequest | undefined>) => {
      state.loading.stuckOrders = true
    },
    fetchStuckOrdersSuccess: (state, action: PayloadAction<StuckOrdersData>) => {
      state.stuckOrders = action.payload
      state.loading.stuckOrders = false
    },
    fetchStuckOrdersFailure: (state, action: PayloadAction<string>) => {
      state.loading.stuckOrders = false
      state.error.message = action.payload
    },

    // Filters
    setDateRange: (state, action: PayloadAction<DateRange>) => {
      state.dateRange = action.payload
    },
    setDatePreset: (state, action: PayloadAction<DatePreset>) => {
      state.dateRange = { preset: action.payload, from: null, to: null }
    },
    setCustomDateRange: (state, action: PayloadAction<{ from: string; to: string }>) => {
      state.dateRange = { preset: 'custom', from: action.payload.from, to: action.payload.to }
    },
    setFilters: (state, action: PayloadAction<QueueDashboardFilters>) => {
      state.filters = action.payload
    },

    // Reset
    resetDashboard: () => initialState,
  },
})

export const queueDashboardActions = queueDashboardSlice.actions
export const queueDashboardReducer = queueDashboardSlice.reducer
